import React, { useMemo, useState } from 'react';
import { Eye, ShieldCheck } from 'lucide-react';
import { Contact, Job, User } from '../types';
import ClientPortalView from './ClientPortalView';

interface Props {
  jobs: Job[];
  contacts: Contact[];
  actingUser?: string;
  onNavigate?: (tab: string) => void;
}

interface ClientOption {
  key: string;
  name: string;
  email: string;
  address: string;
  jobs: Job[];
}

/**
 * Staff-only mirror of the customer portal. It intentionally reuses the same
 * ClientPortalView component the customer sees, so the preview cannot drift
 * from the real UI. Edits are disabled with no-op callbacks.
 */
const StaffClientPreviewView: React.FC<Props> = ({ jobs, contacts, actingUser, onNavigate }) => {
  const clients = useMemo(() => {
    const map = new Map<string, ClientOption>();
    const add = (name: string, email: string, address: string, job?: Job) => {
      const key = (email || name).toLowerCase().trim();
      if (!key) return;
      if (!map.has(key)) map.set(key, { key, name: name || email, email: email || '', address: address || '', jobs: [] });
      if (job) map.get(key)!.jobs.push(job);
    };
    for (const job of jobs) {
      if ((job as any).isDeleted) continue;
      add(job.clientName, job.email, job.address, job);
    }
    for (const contact of contacts) {
      if ((contact as any).isDeleted) continue;
      if (contact.personType && contact.personType !== 'CUSTOMER') continue;
      add(contact.name, contact.email, contact.address || '', undefined);
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [jobs, contacts]);

  const [selectedKey, setSelectedKey] = useState(clients[0]?.key || '');
  const selected = clients.find(client => client.key === selectedKey) || clients[0];

  const previewUser: User | undefined = selected
    ? {
        id: `preview-${selected.key}`,
        name: selected.name,
        email: selected.email,
        role: 'client',
        accountType: 'client',
        address: selected.address,
      }
    : undefined;

  return (
    <div className="space-y-5">
      <div className="bg-bark text-white rounded-[32px] p-6 sm:p-8 relative overflow-hidden">
        <div className="absolute -right-12 -top-12 w-52 h-52 border-[22px] border-white/10 rounded-full" />
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 relative z-10">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.16em] text-gecko flex items-center gap-1.5">
              <Eye className="w-3.5 h-3.5" /> Staff preview
            </p>
            <h1 className="text-2xl sm:text-3xl font-black mt-2">What the customer sees</h1>
            <p className="text-sm text-stone-300 mt-1 max-w-xl">
              This is the live customer portal, rendered for the selected client. It is read-only from this screen, so you can verify wording, status and property data without changing the customer’s record.
            </p>
          </div>
          <div className="w-full md:w-80">
            <label className="text-[10px] font-black uppercase tracking-widest text-stone-300">Preview client</label>
            <select
              value={selected?.key || ''}
              onChange={e => setSelectedKey(e.target.value)}
              className="mt-2 w-full p-3 rounded-xl bg-white text-bark text-sm font-bold outline-none"
            >
              {clients.map(client => (
                <option key={client.key} value={client.key}>
                  {client.name}{client.email ? ` — ${client.email}` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {!selected || !previewUser ? (
        <div className="bg-white rounded-[32px] border border-stone-200 p-10 text-center">
          <ShieldCheck className="w-10 h-10 text-stone-300 mx-auto" />
          <p className="mt-3 text-lg font-black text-bark">No customer records yet</p>
          <p className="text-sm text-stone-500 mt-1">Add a customer or job with an email/name, then return here to preview the portal.</p>
        </div>
      ) : (
        <div className="bg-stone-100/70 rounded-[32px] p-3 sm:p-5 border border-stone-200">
          <div className="flex flex-wrap items-center gap-2 px-2 pb-3 text-[10px] font-bold text-stone-500">
            <span className="px-2.5 py-1 rounded-full bg-white border border-stone-200">Client: {selected.name}</span>
            <span className="px-2.5 py-1 rounded-full bg-white border border-stone-200">{selected.jobs.length} job(s)</span>
            <span className="px-2.5 py-1 rounded-full bg-white border border-stone-200">Read-only preview</span>
            <span className="ml-auto text-stone-400">Acting as {actingUser || 'staff'}</span>
          </div>
          <div className="bg-stone-50 rounded-[28px] p-3 sm:p-6 border border-stone-200">
            <ClientPortalView
              key={selected.key}
              jobs={selected.jobs}
              onUpdate={() => {}}
              onAddLead={() => {}}
              user={previewUser}
              onNavigate={(tab) => {
                if (tab === 'client-catalog' || tab === 'client-consultation') return;
                onNavigate?.(tab);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffClientPreviewView;
